import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Scale, BookOpen, Sparkles, Crown, Check } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import api from '@/services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const processSession = useCallback(async (sessionId) => {
    try {
      const response = await api.post('/auth/process-session', { session_id: sessionId });
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        window.location.hash = '';
        navigate('/dashboard');
      }
    } catch { /* session processing non-critical */ }
  }, [navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const userStr = localStorage.getItem('user');
      
      if (userStr) {
        try {
          const response = await api.get('/auth/me');
          if (response.data) {
            navigate('/dashboard');
          }
        } catch { /* auth check non-critical */ }
      }
    };
    checkAuth();

    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    }
  }, [navigate, processSession]);

  const topics = [
    { key: 'topicUsucapiao' }, { key: 'topicPosse' }, { key: 'topicPropriedade' },
    { key: 'topicPena' }, { key: 'topicDolo' }, { key: 'topicCulpa' },
    { key: 'topicObrigacoes' }, { key: 'topicContratos' }, { key: 'topicSucessao' }, { key: 'topicFamilia' }
  ];

  const plans = [
    {
      nameKey: 'weekly',
      price: 'R$ 25',
      periodKey: 'perWeek',
      featuresKeys: ['detailedSummaries', 'objectiveExamples', 'basicSupport']
    },
    {
      nameKey: 'monthly',
      price: 'R$ 80',
      periodKey: 'perMonth',
      popular: true,
      featuresKeys: ['unlimitedSearch', 'premiumSummaries', 'bookRecommendations', 'advancedExamples']
    },
    {
      nameKey: 'annual',
      price: 'R$ 200',
      periodKey: 'perYear',
      featuresKeys: ['everythingInMonthly', 'save79', 'lifetimeAccess', 'exclusiveContent']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#060B14] via-[#0C1527] to-[#060B14]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scale className="w-8 h-8 text-[#E6C687]" />
            <span className="text-2xl font-bold gradient-text">Law Study A.I</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="header" />
            <Button 
              data-testid="header-login-btn"
              onClick={() => navigate('/auth')}
              className="bg-[#E6C687] text-[#060B14] hover:bg-[#F3D89F] font-semibold px-6"
            >
              {t('login')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="mb-8 inline-block">
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-4 py-2 rounded-full border border-[#E6C687]/30">
              <Sparkles className="w-4 h-4 text-[#E6C687]" />
              <span className="text-sm text-[#E6C687]">{t('aiAssistantFeature')}</span>
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {t('heroTitle')}
          </h1>
          
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            {t('heroDescription')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              data-testid="hero-cta-btn"
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-[#E6C687] text-[#060B14] hover:bg-[#F3D89F] font-semibold px-8 py-6 text-lg"
            >
              {t('getStarted')}
            </Button>
            <Button 
              data-testid="explore-ebooks-btn"
              onClick={() => navigate('/ebooks')}
              size="lg"
              variant="outline"
              className="border-[#E6C687] text-[#E6C687] hover:bg-[#E6C687]/10 px-8 py-6 text-lg"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              {t('ebooks')}
            </Button>
          </div>

          {/* Topic Suggestions */}
          <div className="max-w-4xl mx-auto">
            <p className="text-sm text-gray-400 mb-4">{t('popularTopics')}:</p>
            <div className="flex flex-wrap gap-3 justify-center">
              {topics.map((topic) => (
                <span 
                  key={`topic-${topic.key}`}
                  className="px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 text-sm hover:border-[#E6C687] hover:bg-[#E6C687]/10 cursor-pointer"
                >
                  {t(topic.key)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('featuresTitle')}</h2>
            <p className="text-gray-400">{t('heroDescription')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-effect p-8 rounded-2xl border border-white/10 hover:border-[#E6C687]/50">
              <div className="w-14 h-14 bg-[#E6C687]/20 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-[#E6C687]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('aiAssistantFeature')}</h3>
              <p className="text-gray-400 text-base">
                {t('aiAssistantDesc')}
              </p>
            </div>

            <div className="glass-effect p-8 rounded-2xl border border-white/10 hover:border-[#E6C687]/50">
              <div className="w-14 h-14 bg-[#E6C687]/20 rounded-xl flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-[#E6C687]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('ebooksFeature')}</h3>
              <p className="text-gray-400 text-base">
                {t('ebooksFeatureDesc')}
              </p>
            </div>

            <div className="glass-effect p-8 rounded-2xl border border-white/10 hover:border-[#E6C687]/50">
              <div className="w-14 h-14 bg-[#E6C687]/20 rounded-xl flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-[#E6C687]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('quizFeature')}</h3>
              <p className="text-gray-400 text-base">
                {t('quizFeatureDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('pricingTitle')}</h2>
            <p className="text-gray-400">{t('pricingSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div 
                key={`plan-${plan.nameKey}`}
                className={`glass-effect p-8 rounded-2xl border ${
                  plan.popular 
                    ? 'border-[#E6C687] gold-glow transform scale-105' 
                    : 'border-white/10'
                } relative`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#E6C687] text-[#060B14] px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      {t('mostPopular')}
                    </div>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold mb-2">{t(plan.nameKey)}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold gradient-text">{plan.price}</span>
                  <span className="text-gray-400">{t(plan.periodKey)}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.featuresKeys.map((featureKey) => (
                    <li key={featureKey} className="flex items-start gap-2 text-sm">
                      <Check className="w-5 h-5 text-[#E6C687] flex-shrink-0 mt-0.5" />
                      <span>{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  data-testid={`plan-${plan.nameKey}-btn`}
                  onClick={() => navigate('/auth')}
                  className={`w-full ${
                    plan.popular
                      ? 'bg-[#E6C687] text-[#060B14] hover:bg-[#F3D89F]'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  } font-semibold`}
                >
                  {t('choosePlan')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="container mx-auto text-center text-gray-400">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Scale className="w-6 h-6 text-[#E6C687]" />
            <span className="text-xl font-bold gradient-text">Law Study A.I</span>
          </div>
          <p className="text-sm">{t('allRightsReserved')}</p>
          <p className="text-xs mt-2">{t('availableLanguages')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;